import type {
    ButtonHTMLAttributes,
    CSSProperties,
    MouseEvent,
    PropsWithChildren,
    ReactNode,
} from 'react';
import { useState } from 'react';
import { Link } from 'react-router';
import { cn } from './../../../utils/classname';

type ButtonSize = 'sm' | 'default' | 'lg' | 'small' | 'medium' | 'large' | 'md';

type ResolvedButtonSize = 'sm' | 'default' | 'lg';

const sizeStyles: Record<
    ResolvedButtonSize,
    Pick<CSSProperties, 'padding' | 'gap' | 'fontSize'>
> = {
    sm: {
        padding: '0.25rem 0.5rem',
        gap: '0.2rem',
        fontSize: '0.875rem',
    },
    default: {
        padding: '0.355rem 0.65rem',
        gap: '0.25rem',
        fontSize: undefined,
    },
    lg: {
        padding: '0.5rem 0.9rem',
        gap: '0.35rem',
        fontSize: '1rem',
    },
};

function resolveButtonSize(size: ButtonSize): ResolvedButtonSize {
    if (size === 'small') return 'sm';
    if (size === 'medium' || size === 'md') return 'default';
    if (size === 'large') return 'lg';
    return size;
}

export interface IButtonProps extends PropsWithChildren, ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'light' | 'secondary' | 'danger';
    size?: ButtonSize;
    /**
     * Shows a spinner and ignores activation (a submit button will not submit its form) while the
     * button keeps focus. While loading, `disabled` is exposed as `aria-disabled` instead of the
     * native attribute, so `disabled={isPending}` next to `isLoading={isPending}` is safe.
     */
    isLoading?: boolean;

    leftIcon?: ReactNode;
    rightIcon?: ReactNode;

    borderRadius?: CSSProperties['borderRadius'];
    borderWidth?: CSSProperties['borderWidth'];
    borderColor?: CSSProperties['borderColor'];
    borderStyle?: CSSProperties['borderStyle'];

    bgColor?: CSSProperties['backgroundColor'];
    backgroundColor?: CSSProperties['backgroundColor'];
    textColor?: CSSProperties['color'];
    hoverBackgroundOpacity?: number;

    padding?: CSSProperties['padding'];
    margin?: CSSProperties['margin'];
    gap?: CSSProperties['gap'];

    width?: CSSProperties['width'];
    height?: CSSProperties['height'];

    fontSize?: CSSProperties['fontSize'];
    fontWeight?: CSSProperties['fontWeight'];

    /** Renders a link styled as a button: `http…` is a plain anchor, anything else a router link. */
    href?: string;
    target?: string;
    rel?: string;
}

export default function Button({
    variant = 'primary',
    isLoading = false,
    leftIcon,
    rightIcon,
    children,
    className,
    style,
    type = 'button',
    borderRadius = '6px',
    borderWidth = '1px',
    borderColor,
    borderStyle = 'solid',
    bgColor,
    backgroundColor,
    textColor,
    hoverBackgroundOpacity = 0.9,
    size = 'default',
    padding,
    margin,
    gap,
    width,
    height,
    fontSize = 14,
    fontWeight,
    href,
    target,
    rel,
    onMouseEnter,
    onMouseLeave,
    ...restProps
}: IButtonProps) {
    const { disabled: propDisabled, onClick, ...buttonProps } = restProps;
    const [hovered, setHovered] = useState(false);
    const clampedHoverOpacity = Math.min(Math.max(hoverBackgroundOpacity, 0), 1);
    const sizeStyle = sizeStyles[resolveButtonSize(size)];

    const resolvedPadding = padding ?? sizeStyle.padding;
    const resolvedGap = gap ?? sizeStyle.gap;
    const resolvedFontSize = fontSize ?? sizeStyle.fontSize;

    const resolvedBackgroundColor =
        bgColor ??
        backgroundColor ??
        (variant === 'secondary'
            ? 'var(--color-button-secondary-bg)'
            : variant === 'danger'
              ? 'var(--color-danger)'
              : variant === 'light'
                ? 'color-mix(in srgb, var(--color-button-bg) 3.5%, var(--color-background))'
                : 'var(--color-button-bg)');

    const resolvedBorderColor =
        borderColor ??
        (variant === 'secondary'
            ? 'var(--color-border)'
            : variant === 'danger'
              ? 'var(--color-danger-border)'
              : undefined);

    const resolvedTextColor =
        textColor ??
        (variant === 'secondary'
            ? 'var(--color-text)'
            : variant === 'danger'
              ? '#fff'
              : variant === 'light'
                ? 'var(--color-button-bg)'
                : 'var(--color-button-text)');

    const isDisabled = propDisabled || isLoading;

    const hoverBgColor =
        hovered && !isDisabled
            ? variant === 'secondary'
                ? `color-mix(in srgb, ${resolvedBackgroundColor} 85%, var(--color-bg-primary))`
                : variant === 'danger'
                  ? 'color-mix(in srgb, var(--color-danger) 88%, black)'
                  : `color-mix(in srgb, ${resolvedBackgroundColor} ${Math.round(clampedHoverOpacity * 100)}%, var(--color-bg-primary))`
            : resolvedBackgroundColor;

    const buttonStyle: CSSProperties = {
        display: 'inline-flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        textWrap: 'nowrap',
        justifyContent: 'center',
        position: 'relative',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        transition: 'background-color 0.2s ease, border-color 0.2s ease',
        opacity: isDisabled ? 0.5 : undefined,
        filter: isDisabled ? 'grayscale(100%)' : undefined,
        borderRadius,
        borderWidth,
        borderColor:
            resolvedBorderColor ??
            (hovered && !isDisabled ? hoverBgColor : resolvedBackgroundColor),
        borderStyle,
        backgroundColor: hoverBgColor,
        color: resolvedTextColor,
        padding: resolvedPadding,
        margin,
        gap: resolvedGap,
        width,
        height,
        fontSize: resolvedFontSize,
        fontWeight,
        ...style,
    };

    const spinnerStyle: CSSProperties = {
        width: '1em',
        height: '1em',
        border: '0.15em solid currentColor',
        borderRightColor: 'transparent',
        borderRadius: '50%',
        animation: 'xv-button-spin 0.6s linear infinite',
        flexShrink: 0,
    };

    const renderContent = () => {
        if (isLoading) {
            return (
                <>
                    <span
                        style={{
                            opacity: 0,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: resolvedGap,
                        }}
                    >
                        {leftIcon && (
                            <span
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                {leftIcon}
                            </span>
                        )}
                        {children}
                        {rightIcon && (
                            <span
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                {rightIcon}
                            </span>
                        )}
                    </span>
                    <span style={{ ...spinnerStyle, position: 'absolute' }} />
                </>
            );
        }
        return (
            <>
                {leftIcon && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
                        {leftIcon}
                    </span>
                )}
                {children}
                {rightIcon && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
                        {rightIcon}
                    </span>
                )}
            </>
        );
    };

    if (href) {
        const isExternal = href.startsWith('http');
        const resolvedRel = isExternal
            ? [rel, 'noopener', 'noreferrer'].filter(Boolean).join(' ')
            : rel;
        const linkProps = {
            target,
            rel: resolvedRel || undefined,
            className: cn('xv-button', className),
            style: buttonStyle,
            'aria-disabled': isDisabled ? true : undefined,
            onMouseEnter: (e: MouseEvent<HTMLAnchorElement>) => {
                setHovered(true);
                onMouseEnter?.(e as unknown as MouseEvent<HTMLButtonElement>);
            },
            onMouseLeave: (e: MouseEvent<HTMLAnchorElement>) => {
                setHovered(false);
                onMouseLeave?.(e as unknown as MouseEvent<HTMLButtonElement>);
            },
        };

        return isExternal ? (
            <a href={href} {...linkProps}>
                {renderContent()}
            </a>
        ) : (
            <Link to={href} {...linkProps}>
                {renderContent()}
            </Link>
        );
    }

    return (
        <button
            type={type}
            className={cn('xv-button', className)}
            style={buttonStyle}
            disabled={(propDisabled && !isLoading) || undefined}
            {...buttonProps}
            aria-disabled={isLoading || buttonProps['aria-disabled']}
            aria-busy={isLoading || buttonProps['aria-busy']}
            onClick={(event) => {
                if (isLoading) return event.preventDefault();
                onClick?.(event);
            }}
            onMouseEnter={(event) => {
                setHovered(true);
                onMouseEnter?.(event);
            }}
            onMouseLeave={(event) => {
                setHovered(false);
                onMouseLeave?.(event);
            }}
        >
            {renderContent()}
        </button>
    );
}
